<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import aiReviewsAPI from 'dashboard/api/aiReviews';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import PageHeader from '../components/PageHeader.vue';
import StatusBadge from '../components/StatusBadge.vue';
import VersionHistory from '../components/VersionHistory.vue';
import { getApiErrorMessage } from '../utils';

const { t } = useI18n();
const route = useRoute();

const policy = ref(null);
const selectedVersionId = ref(null);
const isLoading = ref(false);
const isSaving = ref(false);
const isPublishing = ref(false);
const isCreatingVersion = ref(false);
const draftForm = ref({
  instructions: '',
  output_schema_version: '2',
  criteria_json: [],
  issue_types_json: [],
});

const selectedVersion = computed(() =>
  policy.value?.versions?.find(
    version => Number(version.id) === Number(selectedVersionId.value)
  )
);
const draftVersion = computed(() =>
  policy.value?.versions?.find(version => version.state === 'draft')
);
const isDraft = computed(() => selectedVersion.value?.state === 'draft');

watch(selectedVersion, version => {
  if (!version) return;
  draftForm.value = {
    instructions: version.instructions || '',
    output_schema_version: version.output_schema_version || '2',
    criteria_json: (version.criteria_json || []).map(criterion => ({
      ...criterion,
    })),
    issue_types_json: (version.issue_types_json || []).map(issueType => ({
      ...issueType,
    })),
  };
});

const loadPolicy = async preferredVersionId => {
  try {
    isLoading.value = true;
    const { data } = await aiReviewsAPI.getReviewPolicy(route.params.policyId);
    policy.value = data;
    const preferred = data.versions.find(
      version => Number(version.id) === Number(preferredVersionId)
    );
    const initial =
      preferred ||
      data.versions.find(version => version.state === 'draft') ||
      data.active_version ||
      data.versions[0];
    selectedVersionId.value = initial?.id || null;
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isLoading.value = false;
  }
};

const addCriterion = () => {
  draftForm.value.criteria_json.push({
    key: '',
    name: '',
    description: '',
    max_score: 1,
  });
};

const removeCriterion = index => {
  draftForm.value.criteria_json.splice(index, 1);
};

const addIssueType = () => {
  draftForm.value.issue_types_json.push({
    key: '',
    name: '',
    description: '',
  });
};

const removeIssueType = index => {
  draftForm.value.issue_types_json.splice(index, 1);
};

const moveCriterion = (index, offset) => {
  const targetIndex = index + offset;
  if (targetIndex < 0 || targetIndex >= draftForm.value.criteria_json.length) {
    return;
  }
  const [criterion] = draftForm.value.criteria_json.splice(index, 1);
  draftForm.value.criteria_json.splice(targetIndex, 0, criterion);
};

const isCriteriaValid = () => {
  const criteria = draftForm.value.criteria_json;
  const keys = criteria.map(criterion => criterion.key.trim());
  return (
    criteria.length > 0 &&
    keys.every(Boolean) &&
    new Set(keys).size === keys.length &&
    criteria.every(
      criterion => criterion.name.trim() && Number(criterion.max_score) > 0
    )
  );
};

const isIssueTaxonomyValid = () => {
  const issueTypes = draftForm.value.issue_types_json;
  const keys = issueTypes.map(issueType => issueType.key.trim());
  return (
    issueTypes.length > 0 &&
    keys.every(Boolean) &&
    new Set(keys).size === keys.length &&
    issueTypes.every(issueType => issueType.name.trim())
  );
};

const saveDraft = async () => {
  if (!isCriteriaValid()) {
    useAlert(t('AI_REVIEWS.POLICIES.VALIDATION_ERROR'));
    return;
  }
  if (!isIssueTaxonomyValid()) {
    useAlert(t('AI_REVIEWS.POLICIES.ISSUE_TYPES_VALIDATION_ERROR'));
    return;
  }

  try {
    isSaving.value = true;
    const attributes = {
      ...draftForm.value,
      criteria_json: draftForm.value.criteria_json.map(criterion => ({
        ...criterion,
        max_score: Number(criterion.max_score),
      })),
    };
    await aiReviewsAPI.updateReviewPolicyVersion(
      selectedVersion.value.id,
      attributes
    );
    useAlert(t('AI_REVIEWS.POLICIES.SAVE_SUCCESS'));
    await loadPolicy(selectedVersion.value.id);
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isSaving.value = false;
  }
};

const publishDraft = async () => {
  if (
    // eslint-disable-next-line no-alert
    !window.confirm(
      t('AI_REVIEWS.POLICIES.PUBLISH_CONFIRM', {
        version: selectedVersion.value.version_number,
      })
    )
  ) {
    return;
  }

  try {
    isPublishing.value = true;
    const { data } = await aiReviewsAPI.publishReviewPolicyVersion(
      selectedVersion.value.id
    );
    useAlert(t('AI_REVIEWS.POLICIES.PUBLISH_SUCCESS'));
    await loadPolicy(data.id);
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isPublishing.value = false;
  }
};

const createVersion = async () => {
  try {
    isCreatingVersion.value = true;
    const { data } = await aiReviewsAPI.createReviewPolicyVersion(
      policy.value.id,
      selectedVersion.value?.id
    );
    useAlert(t('AI_REVIEWS.POLICIES.VERSION_CREATE_SUCCESS'));
    await loadPolicy(data.id);
  } catch (error) {
    useAlert(getApiErrorMessage(error, t('AI_REVIEWS.COMMON.API_ERROR')));
  } finally {
    isCreatingVersion.value = false;
  }
};

onMounted(() => loadPolicy());
</script>

<template>
  <div v-if="isLoading && !policy" class="p-10 text-center text-n-slate-11">
    {{ t('AI_REVIEWS.COMMON.LOADING') }}
  </div>
  <template v-else-if="policy">
    <PageHeader
      :title="policy.name"
      :description="
        policy.description || t('AI_REVIEWS.POLICIES.DETAIL_DESCRIPTION')
      "
    >
      <template #actions>
        <Button
          v-if="!draftVersion"
          :label="t('AI_REVIEWS.POLICIES.CREATE_VERSION')"
          icon="i-lucide-copy-plus"
          :is-loading="isCreatingVersion"
          @click="createVersion"
        />
      </template>
    </PageHeader>

    <div class="grid gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
      <VersionHistory
        :title="t('AI_REVIEWS.POLICIES.VERSION_HISTORY')"
        :versions="policy.versions"
        :selected-id="selectedVersionId"
        @select="selectedVersionId = $event"
      />

      <section
        v-if="selectedVersion"
        class="p-5 border rounded-xl border-n-weak bg-n-solid-2"
      >
        <div class="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div class="flex items-center gap-2">
            <h3 class="mb-0 text-lg font-semibold text-n-slate-12">
              {{
                t('AI_REVIEWS.COMMON.VERSION', {
                  version: selectedVersion.version_number,
                })
              }}
            </h3>
            <StatusBadge :status="selectedVersion.state" />
          </div>
          <div v-if="isDraft" class="flex gap-2">
            <Button
              :label="t('AI_REVIEWS.POLICIES.SAVE_DRAFT')"
              color="slate"
              variant="faded"
              :is-loading="isSaving"
              @click="saveDraft"
            />
            <Button
              :label="t('AI_REVIEWS.COMMON.PUBLISH')"
              :is-loading="isPublishing"
              @click="publishDraft"
            />
          </div>
        </div>

        <p
          v-if="!isDraft"
          class="p-3 mb-5 text-sm rounded-lg bg-n-alpha-1 text-n-slate-11"
        >
          {{ t('AI_REVIEWS.POLICIES.READ_ONLY_NOTICE') }}
        </p>

        <div class="grid gap-4">
          <Textarea
            id="ai-review-policy-instructions"
            v-model="draftForm.instructions"
            :label="t('AI_REVIEWS.POLICIES.INSTRUCTIONS')"
            :disabled="!isDraft"
            resize
            custom-text-area-class="min-h-32"
          />
          <Input
            id="ai-review-output-schema"
            v-model="draftForm.output_schema_version"
            :label="t('AI_REVIEWS.POLICIES.OUTPUT_SCHEMA')"
            :disabled="!isDraft"
          />

          <div class="flex items-center justify-between mt-2">
            <h4 class="mb-0 text-base font-semibold text-n-slate-12">
              {{ t('AI_REVIEWS.POLICIES.CRITERIA') }}
            </h4>
            <Button
              v-if="isDraft"
              :label="t('AI_REVIEWS.POLICIES.ADD_CRITERION')"
              icon="i-lucide-plus"
              color="slate"
              variant="faded"
              size="sm"
              @click="addCriterion"
            />
          </div>

          <div
            v-for="(criterion, index) in draftForm.criteria_json"
            :key="index"
            class="grid gap-3 p-4 border rounded-lg border-n-weak md:grid-cols-2"
          >
            <Input
              :id="`criterion-key-${index}`"
              v-model="criterion.key"
              :label="t('AI_REVIEWS.POLICIES.CRITERION_KEY')"
              :disabled="!isDraft"
            />
            <Input
              :id="`criterion-name-${index}`"
              v-model="criterion.name"
              :label="t('AI_REVIEWS.POLICIES.CRITERION_NAME')"
              :disabled="!isDraft"
            />
            <Textarea
              :id="`criterion-description-${index}`"
              v-model="criterion.description"
              :label="t('AI_REVIEWS.POLICIES.CRITERION_DESCRIPTION')"
              :disabled="!isDraft"
              resize
            />
            <Input
              :id="`criterion-max-${index}`"
              v-model="criterion.max_score"
              type="number"
              min="0.01"
              :label="t('AI_REVIEWS.POLICIES.CRITERION_MAX_SCORE')"
              :disabled="!isDraft"
            />
            <div v-if="isDraft" class="flex justify-end gap-1 md:col-span-2">
              <Button
                icon="i-lucide-arrow-up"
                color="slate"
                variant="ghost"
                size="sm"
                :aria-label="
                  t('AI_REVIEWS.POLICIES.MOVE_CRITERION_UP', {
                    number: index + 1,
                  })
                "
                :disabled="index === 0"
                @click="moveCriterion(index, -1)"
              />
              <Button
                icon="i-lucide-arrow-down"
                color="slate"
                variant="ghost"
                size="sm"
                :aria-label="
                  t('AI_REVIEWS.POLICIES.MOVE_CRITERION_DOWN', {
                    number: index + 1,
                  })
                "
                :disabled="index === draftForm.criteria_json.length - 1"
                @click="moveCriterion(index, 1)"
              />
              <Button
                icon="i-lucide-trash-2"
                color="ruby"
                variant="ghost"
                size="sm"
                :aria-label="
                  t('AI_REVIEWS.POLICIES.REMOVE_CRITERION', {
                    number: index + 1,
                  })
                "
                @click="removeCriterion(index)"
              />
            </div>
          </div>

          <details class="mt-3 border rounded-lg border-n-weak">
            <summary
              class="flex items-center justify-between gap-3 p-4 text-base font-semibold cursor-pointer text-n-slate-12"
            >
              <span>{{ t('AI_REVIEWS.POLICIES.ISSUE_TYPES') }}</span>
              <span class="text-sm font-normal text-n-slate-11">
                {{
                  t('AI_REVIEWS.POLICIES.ISSUE_TYPES_COUNT', {
                    count: draftForm.issue_types_json.length,
                  })
                }}
              </span>
            </summary>
            <div class="grid gap-3 p-4 pt-0">
              <p class="mb-0 text-sm text-n-slate-11">
                {{ t('AI_REVIEWS.POLICIES.ISSUE_TYPES_HELP') }}
              </p>
              <div v-if="isDraft" class="flex justify-end">
                <Button
                  :label="t('AI_REVIEWS.POLICIES.ADD_ISSUE_TYPE')"
                  icon="i-lucide-plus"
                  color="slate"
                  variant="faded"
                  size="sm"
                  @click="addIssueType"
                />
              </div>
              <div
                v-for="(issueType, index) in draftForm.issue_types_json"
                :key="index"
                class="grid gap-3 p-4 border rounded-lg border-n-weak md:grid-cols-2"
              >
                <Input
                  :id="`issue-type-key-${index}`"
                  v-model="issueType.key"
                  :label="t('AI_REVIEWS.POLICIES.ISSUE_TYPE_KEY')"
                  :disabled="!isDraft"
                />
                <Input
                  :id="`issue-type-name-${index}`"
                  v-model="issueType.name"
                  :label="t('AI_REVIEWS.POLICIES.ISSUE_TYPE_NAME')"
                  :disabled="!isDraft"
                />
                <Textarea
                  :id="`issue-type-description-${index}`"
                  v-model="issueType.description"
                  class="md:col-span-2"
                  :label="t('AI_REVIEWS.POLICIES.ISSUE_TYPE_DESCRIPTION')"
                  :disabled="!isDraft"
                  resize
                />
                <div v-if="isDraft" class="flex justify-end md:col-span-2">
                  <Button
                    icon="i-lucide-trash-2"
                    color="ruby"
                    variant="ghost"
                    size="sm"
                    :aria-label="
                      t('AI_REVIEWS.POLICIES.REMOVE_ISSUE_TYPE', {
                        number: index + 1,
                      })
                    "
                    @click="removeIssueType(index)"
                  />
                </div>
              </div>
            </div>
          </details>
        </div>
      </section>
    </div>
  </template>
</template>
